/**
 * Hides product cards outside the selected delivery week and applies availability
 * badges. Cards are RENDERED by Liquid and hidden here -- Liquid never computes a
 * week, so the cutoff/blackout rule is never duplicated into a third language.
 */
(function () {
  var HIDDEN = 'availability-hidden';

  // card-product.liquid's .card-wrapper (data-product-card) sits inside <li
  // class="grid__item"> or <div class="collage__item">; week-shelf-item.liquid's own
  // root carries data-product-card too, but IS wrapped -- every render site
  // (sections/main-shop-all.liquid, main-collection.liquid, main-gifting.liquid,
  // main-collection-product-grid-categories.liquid) puts it inside a per-product
  // <div class="week-shelf"> grid cell (assets/base.css's `.week` is display:grid).
  // Hiding and counting both need to resolve the same "thing that actually occupies a
  // grid slot" for a given card, so this selector is shared between them rather than
  // duplicated. `:not(.week-shelf-product)` excludes the one site where `.week-shelf`
  // wraps an entire ROW of cards instead of a single one -- sections/related-products.liquid's
  // `.week-shelf.week-shelf-product` -- so hiding one out-of-week recommendation there
  // can't take the whole row down with it.
  var CARD_WRAPPER_SELECTOR = '.grid__item, .collage__item, li, .week-shelf:not(.week-shelf-product)';

  // Matches the token snippets/availability-count.liquid embeds in a
  // data-availability-count-token phrase (see that file for why: the "X of Y
  // products" phrasing pluralizes on Y, not X, so Liquid renders once with the real,
  // unchanged Y and a placeholder standing in for X).
  var COUNT_TOKEN = '§availability-count§';

  // A count element carries one of two mutually-exclusive sets of pre-rendered forms
  // (see snippets/availability-count.liquid for how/why): either a non-pluralizing
  // token to substitute directly, or a real "count: 1" / "count: 2" phrase pair to
  // choose between and then substitute the sample digit within. Never build "N
  // products" out of a bare number here -- that discards the label and, for
  // non-English locales, the pluralization entirely.
  function countTextFor(el, visible) {
    var token = el.getAttribute('data-availability-count-token');
    if (token) return token.replace(COUNT_TOKEN, String(visible));

    var one = el.getAttribute('data-availability-count-one');
    var other = el.getAttribute('data-availability-count-other');
    if (one && other) {
      return visible === 1 ? one.replace('1', String(visible)) : other.replace('2', String(visible));
    }

    // No pre-rendered forms were supplied -- shouldn't happen once every count
    // element goes through the availability-count snippet, but fail open with a bare
    // number rather than leaving a stale, unfiltered count on screen.
    return String(visible);
  }

  function readCard(el) {
    return window.StockedDeliveryFormat.parseAvailability({
      startWeek: el.dataset.availabilityStart || null,
      endWeek: el.dataset.availabilityEnd || null,
      weeks: (el.dataset.availabilityWeeks || '').split(',').filter(Boolean),
      dates: (el.dataset.availabilityDates || '').split(',').filter(Boolean),
    });
  }

  // "Last chance" is a direct test against the product's own bound -- NOT "no later
  // week is open in the visible horizon", which would false-positive on every
  // unbounded product whenever the customer picks the last horizon week, and on any
  // bounded product whose real end lies beyond the horizon.
  function badgeFor(availability, week) {
    if (availability.dates) return 'special-date';
    if (availability.weeks) return 'special';
    if (availability.end && week === availability.end) return 'last-chance';
    return null;
  }

  // The special-date badge names one date: the first of the product's dates that is
  // open now, else the first one that is at least configured. Its text is filled here
  // because Liquid can't know which dates are open. An unresolvable product (only
  // unconfigured dates) gets no badge rather than an empty pill. labelOnly: just the
  // date's label ("Thanksgiving"), for a page whose banner already gives the date;
  // a date with no label keeps the full title.
  function specialBadge(el, availability, openISOs, labelOnly) {
    var delivery = window.StockedDelivery;
    if (!delivery) return '';
    var iso = window.StockedDeliveryFormat.datesOffered([availability], openISOs)[0] ||
      availability.dates.filter(function (d) { return delivery.slotOf(d) === d; })[0];
    // slotLabel() of a special date is its "Thanksgiving · Wed Nov 25" title.
    var title = iso ? ((labelOnly && delivery.specialDateLabel(iso)) || delivery.slotLabel(iso)) : '';
    Array.prototype.forEach.call(
      el.querySelectorAll('[data-badge="special-date"] [data-badge-text]'),
      function (node) { node.textContent = title; }
    );
    return title ? badgeFor(availability) : '';
  }

  // ?delivery_week=DS+2635 renders the catalog as that week. PREVIEW ONLY: it never
  // writes to localStorage or the cart, and add-to-cart still resolves against the real
  // selection (product-form.js only ever reads StockedDelivery.getSelection() /
  // requireSelection(), never this module or its data-availability-* output), so a
  // preview session cannot produce a mis-dated order. The `+` in the query string is a
  // URL-encoded space; URLSearchParams.get() decodes that for us, so the raw value
  // handed to resolvePreviewWeek() is already "DS 2635" -- no decoding of our own.
  // Horizon validation (not just parsing) lives in the pure, unit-tested
  // assets/delivery-week-preview.js -- see that file for why an out-of-horizon code
  // must not fall back to anything.
  function previewWeek() {
    var raw = new URLSearchParams(window.location.search).get('delivery_week');
    var fmt = window.StockedDeliveryFormat;
    var delivery = window.StockedDelivery;
    if (!raw || !fmt || !delivery || !window.StockedDeliveryWeekPreview) return null;
    return window.StockedDeliveryWeekPreview.resolvePreviewWeek(
      raw,
      delivery.selectableWeeks(),
      fmt.parseWeekCode
    );
  }

  // A card counts as hidden when its grid-slot wrapper carries EITHER filter's
  // class: this module's availability-hidden or assets/dietary-filter.js's
  // dietary-hidden. Each filter only ever toggles its own class, so neither can
  // un-hide what the other hid -- this is the one place they are combined.
  //
  // Hiding targets the card's grid-item wrapper, not the element carrying
  // data-product-card, so a naive "[data-product-card]:not(.HIDDEN)" query would
  // count cards whose *wrapper* is hidden while the card itself stays classless.
  // Walk the same closest() the hiding code uses instead.
  var HIDDEN_CLASSES = [HIDDEN, 'dietary-hidden'];

  function isCardHidden(el) {
    var target = el.closest(CARD_WRAPPER_SELECTOR) || el;
    for (var i = 0; i < HIDDEN_CLASSES.length; i += 1) {
      if (target.classList.contains(HIDDEN_CLASSES[i])) return true;
    }
    return false;
  }

  // Counts and empty states must reflect what the customer can actually see, not
  // what Liquid rendered. Public (window.StockedAvailability.refreshScopes) so the
  // diet filter can rerun it after its own pass -- one recount, two filters.
  function refreshScopes() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-availability-scope]'),
      function (scope) {
        // Search lists catering (dated) products but leaves them out of its count --
        // the count describes the menu. They still count as visible for the empty
        // state and collapse below, or a catering-only result page would hide itself.
        var excludeDated = scope.getAttribute('data-availability-scope') === 'search';
        var visible = 0;
        var counted = 0;
        Array.prototype.forEach.call(scope.querySelectorAll('[data-product-card]'), function (card) {
          if (isCardHidden(card)) return;
          visible += 1;
          if (!(excludeDated && card.dataset.availabilityDates)) counted += 1;
        });

        // A scope can contain more than one count element at once -- horizontal and
        // drawer filter types render a desktop AND a mobile copy simultaneously,
        // toggled by CSS per breakpoint rather than by Liquid -- so every match must
        // be updated, not just the first.
        Array.prototype.forEach.call(
          scope.querySelectorAll('[data-availability-count]'),
          function (countEl) {
            countEl.textContent = countTextFor(countEl, counted);
          }
        );

        var emptyEl = scope.querySelector('[data-availability-empty]');
        if (emptyEl) emptyEl.hidden = visible !== 0;

        // A scope collapses only through an explicit data-availability-collapse
        // target: itself, for scopes with nothing else worth keeping around (a
        // shop-all block -- heading, description and cards all go together), or a
        // descendant, for scopes whose count/filters/empty-message must stay visible
        // even when the product list collapses (the collection grid).
        var collapseTarget = scope.hasAttribute('data-availability-collapse')
          ? scope
          : scope.querySelector('[data-availability-collapse]');
        if (collapseTarget) {
          collapseTarget.classList.toggle('availability-scope-empty', visible === 0);
        }
      }
    );

    // Collapsing a scope can remove a shop-all block from view; its subnav entry
    // must go with it. No global handle is needed -- the element is already in the
    // DOM by the time apply() runs (deferred scripts execute before DOMContentLoaded).
    var scrollSpy = document.querySelector('scroll-spy-nav');
    if (scrollSpy && typeof scrollSpy.refresh === 'function') {
      scrollSpy.refresh();
    }
  }

  function apply() {
    var week;
    try {
      // preview, when active, stands in for the real selection everywhere below --
      // cards, counts, badges, and the PDP block all key off `week`, so every one of
      // them renders as the previewed week would look. Nothing here reads or writes
      // the real selection (getSelection() is only consulted as the NON-preview
      // fallback), so the customer's actual stored date is untouched either way.
      var preview = previewWeek();
      var delivery = window.StockedDelivery;
      var sel = delivery && delivery.getSelection();
      var fmt = window.StockedDeliveryFormat;
      var specialSurface = document.body.hasAttribute('data-special-surface');
      // A slot: the week code, or the ISO itself when the selection is a special date.
      // Regular surfaces never render a special date's (empty) menu: a catering cart
      // sees the first regular week's menu, with the delivery bar's notice saying why.
      week = preview || (sel
        ? (specialSurface ? delivery.slotOf(sel.iso) : delivery.menuSlot())
        : null);
      var openSpecialISOs = delivery
        ? delivery.openSpecialDates().map(function (e) { return e.iso; })
        : [];
      var cards = document.querySelectorAll('[data-product-card]');

      Array.prototype.forEach.call(cards, function (el) {
        // Hiding only el would leave its wrapper occupying an empty slot in the grid;
        // hide the outermost item instead. el itself is used when there is no such
        // wrapper (week-shelf-item).
        var target = el.closest(CARD_WRAPPER_SELECTOR) || el;
        var availability = readCard(el);

        // Products with deliver_dates (catering) aren't part of the menu: hidden on
        // every regular surface whatever the selection -- including none, so this runs
        // before the no-week early return. Search always lists them (finding "turkey"
        // should find the turkey); a special surface (the catering collection) shows
        // the ones listing any CONFIGURED special date, open or not: before `opens` and
        // after the cutoff the grid stays up and the special-date banner disables its
        // add buttons instead (assets/special-date-banner.js). Spec §5.1-5.3.
        if (availability.dates) {
          var shown = el.closest('[data-availability-scope="search"]')
            ? true
            : specialSurface && !!delivery && availability.dates.some(function (d) {
              return delivery.slotOf(d) === d;
            });
          target.classList.toggle(HIDDEN, !shown);
          // Badged only in search: on the catering collection the banner already names
          // the date, so a per-card badge would just repeat it.
          el.setAttribute('data-availability-badge', shown && !specialSurface ? specialBadge(el, availability, openSpecialISOs) : '');
          return;
        }

        // A special surface lists only dated (catering) products: a regular product
        // dropped into the catering collection by mistake stays hidden whatever the
        // selection, so it can't be added from there (spec §5.1).
        if (specialSurface) {
          target.classList.add(HIDDEN);
          el.setAttribute('data-availability-badge', '');
          return;
        }

        // With no week chosen yet nothing is filtered: the catalog behind the gate is
        // the unfiltered one, and the gate is what stops interaction.
        if (!week) {
          target.classList.remove(HIDDEN);
          el.setAttribute('data-availability-badge', '');
          return;
        }
        var state = fmt.availabilityFor(availability, week);
        target.classList.toggle(HIDDEN, state !== 'available');
        var badge = state === 'available' ? badgeFor(availability, week) : null;
        el.setAttribute('data-availability-badge', badge || '');
      });

      refreshScopes();

      // The PDP (sections/main-product.liquid) is the one surface that stays reachable
      // out-of-week -- inbound social/paid/email traffic lands here, and those products
      // are genuinely live. So the page always renders in full; only the add-to-cart
      // affordance changes. It's not a [data-product-card], so the loop above never sets
      // its badge -- do that here too, or the PDP badge stays permanently inert.
      var pdp = document.querySelector('[data-product-availability]');
      var pdpAvailability = pdp ? readCard(pdp) : null;
      if (pdpAvailability && pdpAvailability.dates) {
        // A dated (catering) PDP has no week state or switch-week affordance -- its
        // banner and add-to-cart states are its own (spec §5.4). Where that banner shows
        // (a special surface) it already gives the date, so the badge is just the label.
        pdp.setAttribute('data-availability-badge', specialBadge(pdp, pdpAvailability, openSpecialISOs, specialSurface));
      } else if (pdp && week) {
        var pdpState = fmt.availabilityFor(pdpAvailability, week);
        pdp.setAttribute('data-availability-state', pdpState);
        pdp.setAttribute('data-availability-badge', pdpState !== 'available'
          ? ''
          : badgeFor(pdpAvailability, week) || '');

        var switchBtn = pdp.querySelector('[data-availability-switch]');
        if (switchBtn) {
          if (pdpState === 'available') {
            switchBtn.hidden = true;
            delete switchBtn.dataset.availabilityTargetWeek;
          } else {
            var openWeeks = fmt.availableWeeks(pdpAvailability, window.StockedDelivery.selectableWeeks());
            // "not_yet" -> the soonest open week; "ended" -> the latest open week
            // still within the horizon (matches badgeFor()'s own not-later-than-
            // the-horizon reasoning: never propose a week the horizon doesn't offer).
            // 'unavailable' (the selection is a special date) -> the soonest open week too.
            var targetWeek = pdpState === 'ended' ? openWeeks[openWeeks.length - 1] : openWeeks[0];
            // windowLabelForWeek() is the SAME helper the reconcile dialog uses
            // (assets/delivery-date.js) -- reused here rather than re-deriving
            // weekBounds/deliveryWindowLabel a second time, so the two surfaces can't
            // drift on how a window label is formatted. It returns '' for a week code
            // it can't resolve bounds for, which doubles as this block's hide signal.
            var windowLabel = targetWeek ? window.StockedDelivery.windowLabelForWeek(targetWeek) : '';
            if (windowLabel) {
              switchBtn.textContent = 'Available ' + windowLabel + ' — switch to that week?';
              switchBtn.dataset.availabilityTargetWeek = targetWeek;
              switchBtn.hidden = false;
            } else {
              // No open week in the current horizon covers this product (e.g. an
              // "ended" special with nothing left to switch to) -- nothing to offer.
              switchBtn.hidden = true;
              delete switchBtn.dataset.availabilityTargetWeek;
            }
          }
        }
      }

      // High-contrast strip so a preview session can never be mistaken for the live
      // storefront. Lives inside the try, above the catch -- so a failure composing
      // the label is caught by the SAME fail-open catch as the rest of apply() (an
      // uncaught throw here must not skip the stocked:availability-applied dispatch in
      // the finally below, see the module comment on that dispatch). Unconditional
      // hidden/shown assignment (not just on preview) so a URL that HAD a preview and
      // then loses it (e.g. the merchant edits the address bar) doesn't leave a stale
      // banner from the previous apply() run.
      var banner = document.getElementById('StockedPreviewBanner');
      if (banner) {
        banner.hidden = !preview;
        if (preview) {
          var label = banner.querySelector('[data-preview-label]');
          if (label) {
            label.textContent = 'Previewing delivery week ' + preview + ' — not the live storefront.';
          }
        }
      }
    } catch (err) {
      // Fail OPEN: an unfiltered-but-visible catalog can't produce a bad order (the
      // checkout function is the real enforcement boundary), but a silently-thrown
      // apply() that never dispatches stocked:availability-applied would leave
      // .availability-pending on <body> forever, hiding every product on the site.
      console.error('[StockedAvailability] apply() failed; catalog left unfiltered', err);
    } finally {
      document.dispatchEvent(new CustomEvent('stocked:availability-applied', {
        detail: { week: week },
      }));
    }
  }

  window.StockedAvailability = {
    apply: apply,
    // The special-date banner (assets/special-date-banner.js) reads the page's
    // cards the same way.
    readCard: readCard,
    previewWeek: previewWeek,
    refreshScopes: refreshScopes,
    isCardHidden: isCardHidden,
    CARD_WRAPPER_SELECTOR: CARD_WRAPPER_SELECTOR,
  };
  document.addEventListener('DOMContentLoaded', apply);
  document.addEventListener('stocked:week-changed', apply);

  // Switch-week affordance (see apply() above for where the button's target week is
  // computed). Registered once at module scope, not inside apply() -- apply() can rerun
  // many times per page (every stocked:week-changed) and this only needs to bind once.
  //
  // Must land on a real SELECTABLE date in the target week, not just the week code --
  // select()/reconcileForDate() both operate on a specific ISO date. And it must reconcile
  // BEFORE committing: switching the week can leave cart lines (added under the old week)
  // that can't come the new one, and Task 13 built reconcileForDate() for exactly this --
  // see selectDate() in assets/delivery-date.js, which this mirrors. Same partial-failure
  // invariant as every other reconcileForDate() caller: removeLines() can reject after
  // already removing SOME lines server-side (Error with .removedCount > 0), so a reload is
  // required whenever result.removed is true, regardless of whether the date itself ends
  // up applied.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-availability-switch]');
    if (!btn || btn.hidden || btn.disabled) return;
    var targetWeek = btn.dataset.availabilityTargetWeek;
    var delivery = window.StockedDelivery;
    var fmt = window.StockedDeliveryFormat;
    if (!targetWeek || !delivery || !fmt || btn.dataset.availabilitySwitching) return;

    var match = null;
    var dates = delivery.selectableDates();
    for (var i = 0; i < dates.length; i += 1) {
      if (fmt.weekCodeOf(dates[i].iso) === targetWeek) {
        match = dates[i];
        break;
      }
    }
    // The target week closed (cutoff elapsed) in between apply() computing it and this
    // click -- nothing to switch to right now. The next apply() run (stocked:week-changed
    // or a fresh page load) will re-derive a fresh target, or hide the button entirely.
    if (!match) return;

    btn.dataset.availabilitySwitching = '1';
    btn.disabled = true;

    function stopSwitching() {
      delete btn.dataset.availabilitySwitching;
      btn.disabled = false;
    }

    delivery.reconcileForDate(match.iso).then(function (result) {
      if (!result.proceed) {
        stopSwitching();
        if (result.removed) location.reload();
        return;
      }
      var sel = delivery.select(match.iso);
      if (!sel) {
        // The picked date itself stopped being selectable in the few seconds the
        // reconcile dialog was open. warnDateNotApplied() (shared with every other
        // reconcileForDate() caller in assets/delivery-date.js) logs, alerts, and
        // reloads -- silence here would leave the customer thinking their switch
        // went through when only the (destructive) removal half did.
        stopSwitching();
        if (result.removed) delivery.warnDateNotApplied();
        return;
      }
      var persistence = delivery.setCartAttributes(sel);
      // The delivery-date:changed bridge in assets/delivery-date.js picks this up and
      // re-fires stocked:week-changed (which apply() above listens for) only if the
      // week actually changed -- see that file's bridge comment. Every commit path,
      // including this one, dispatches only this one event now.
      document.dispatchEvent(new CustomEvent('delivery-date:changed', {
        detail: { date: sel.iso, label: sel.label },
      }));
      if (result.removed) {
        // Wait for the attribute write before reloading -- location.reload() aborts
        // it in flight, which would remove the conflicting lines and then silently
        // lose the new date. Mirrors reloadAfter() in assets/delivery-date.js.
        Promise.resolve(persistence).catch(function () {}).then(function () { location.reload(); });
        return;
      }
      stopSwitching();
    }).catch(function (err) {
      // reconcileForDate() already fails open internally, but guard the outer promise
      // too -- a thrown error here must never leave the button stuck disabled.
      console.error('[StockedAvailability] switch-week failed', err);
      stopSwitching();
    });
  });
}());
